import React, { useState, useEffect } from 'react';
import styled, { useTheme, css } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useFieldAuth } from '../contexts/FieldContext';
import { useNotification } from '../hooks/useNotification';
import { getPublishedNews } from '../services/adminService';
import { getOperations } from '../services/operationService';
import { Operation } from '../types/operation';
import { NewsItem } from '../types/news';
import clanService from '../services/clanService';
import { Clan } from '../types/clan';
import NotificationContainer from '../components/common/NotificationContainer';
import ModernBackground from '../components/common/ModernBackground';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const cardBackground = (theme: any) => theme.profile.cards.background || theme.colors.surface;

const cardBorder = (theme: any) => theme.profile.cards.border || theme.colors.borderLight;

const cardShadow = (theme: { profile: { cards: { shadow: string } }; shadows: { card: string } }) =>
  theme.profile.cards.shadow || theme.shadows.card;

const shadowGridChrome = css`
  ${({ theme }) => theme.name === 'shadowGrid' && css`
    position: relative;
    border: 1px solid rgba(0, 212, 255, 0.35);
    background: ${({ theme }) => cardBackground(theme)};
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.6), 0 0 28px rgba(0, 212, 255, 0.2);
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(0, 212, 255, 0.2);
      opacity: 0.25;
      pointer-events: none;
    }
  `}
`;

const PageBackground = styled.div<{ $shadow?: boolean }>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  padding: 20px;
  padding-top: 100px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }
`;

const DottedOverlay = styled.div`
  display: none;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  z-index: 3;
  font-family: ${({ theme }) => theme.fonts.body};
  
  @media (max-width: 1240px) {
    max-width: 95%;
  }
`;

const HeroSection = styled.section`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 16px;
  padding: 60px 40px;
  text-align: center;
  margin-bottom: 50px;
  position: relative;
  overflow: hidden;
  box-shadow: ${({ theme }) => cardShadow(theme)};
  ${shadowGridChrome};

  @media (max-width: 768px) {
    padding: 40px 20px;
    margin-bottom: 30px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 20px;
  text-shadow: 0 0 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.7)};
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  z-index: 1;
  font-family: ${({ theme }) => theme.fonts.heading};

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 30px;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 1;
  font-family: ${({ theme }) => theme.fonts.body};

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
    padding: 0 10px;
    margin-bottom: 20px;
  }
`;

const HeroButton = styled(Link)`
  display: inline-block;
  padding: 14px 32px;
  background: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.styles.buttonBorderRadius};
  color: #00121f;
  font-size: 1.1rem;
  font-weight: bold;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.button};
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 1rem;
  }
`;

const Section = styled.section`
  margin-bottom: 50px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 10px;
  text-shadow: 0 0 10px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  text-transform: uppercase;
  letter-spacing: 2px;
  font-family: ${({ theme }) => theme.fonts.heading};

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: ${({ theme }) => theme.fonts.body};

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

// News/Intel Styles
const IntelCard = styled(Link)`
  display: block;
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 30px;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: ${({ theme }) => cardShadow(theme)};
  ${shadowGridChrome};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => `linear-gradient(90deg, transparent, ${withAlpha(theme.colors.primary, 0.1)}, transparent)`};
    transition: left 0.5s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadows.card};

    &::before {
      left: 100%;
    }
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const IntelTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 10px;
  font-family: ${({ theme }) => theme.fonts.heading};

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const IntelDate = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
  letter-spacing: 1px;
`;

// Mission Styles
const MissionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const MissionCard = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => cardShadow(theme)};
  ${shadowGridChrome};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadows.card};
  }
`;

const MissionHeader = styled(Link)`
  text-decoration: none;
  margin-bottom: 15px;
`;

const MissionCode = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 5px;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
`;

const MissionName = styled.h3`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 10px;
  font-family: ${({ theme }) => theme.fonts.heading};

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const MissionDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 20px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MissionFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const MissionPrice = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const MissionButton = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  color: #00121f;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.button};
  }
`;

// Clan Styles
const ClansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 25px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ClanCard = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: ${({ theme }) => cardShadow(theme)};
  ${shadowGridChrome};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadows.card};
  }
`;

const ClanType = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  margin-bottom: 10px;
  font-family: 'Courier New', monospace;
`;

const ClanName = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 5px;
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const ClanTag = styled.div`
  display: inline-block;
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.15)};
  color: ${({ theme }) => theme.colors.primary};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.9rem;
  margin-bottom: 15px;
`;

const ClanStats = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 20px 0;
`;

const ClanStat = styled.div`
  text-align: center;
`;

const ClanStatValue = styled.div`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: bold;
`;

const ClanStatLabel = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ClanButton = styled(Link)`
  display: inline-block;
  padding: 10px 20px;
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.15)};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.25)};
    transform: translateY(-1px);
  }
`;

// Field Styles
const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FieldCard = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 25px;
  transition: all 0.3s ease;
  box-shadow: ${({ theme }) => cardShadow(theme)};
  ${shadowGridChrome};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadows.card};
  }
`;

const FieldTitle = styled.h3`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 15px;
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const FieldDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const FieldButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const FieldButton = styled(Link)`
  flex: 1;
  min-width: 120px;
  padding: 10px 15px;
  background: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  color: #00121f;
  text-align: center;
  text-decoration: none;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.button};
  }

  @media (max-width: 480px) {
    flex: 1 1 100%;
  }
`;

const FieldButtonDisabled = styled.button`
  flex: 1;
  min-width: 120px;
  padding: 10px 15px;
  background: rgba(136, 136, 136, 0.2);
  border: 2px solid rgba(136, 136, 136, 0.4);
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  font-weight: bold;
  cursor: not-allowed;
  transition: all 0.3s ease;

  @media (max-width: 480px) {
    flex: 1 1 100%;
  }
`;

const ViewAllButton = styled(Link)`
  display: inline-block;
  padding: 12px 28px;
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: bold;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${({ theme }) => theme.fonts.heading};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.border};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.button};
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.2rem;
  padding: 40px 0;
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-family: 'Courier New', monospace;
`;

const HomePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [featuredMissions, setFeaturedMissions] = useState<Operation[]>([]);
  const [latestNews, setLatestNews] = useState<NewsItem[]>([]);
  const [topAirsoftClan, setTopAirsoftClan] = useState<Clan | null>(null);
  const [topPaintballClan, setTopPaintballClan] = useState<Clan | null>(null);
  const [clansLoading, setClansLoading] = useState(true);
  const { addItem } = useCart();
  const { user } = useAuth();
  const { field, isFieldAuthenticated, initializing } = useFieldAuth();
  const { notifications, removeNotification, showError } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isShadowGrid = theme.name === 'shadowGrid';
  const isModernWarfare = theme.name === 'modernWarfare';
  const isNightOps = theme.name === 'nightOps';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Получаем последние новости
        const newsResponse = await getPublishedNews();
        if (newsResponse && newsResponse.news) {
          setLatestNews(newsResponse.news.slice(0, 1));
        }
        
        // Получаем избранные миссии
        const operationsResponse = await getOperations();
        if (operationsResponse && operationsResponse.operations) {
          const featured = operationsResponse.operations
            .filter((operation: Operation) => operation.isFeatured)
            .slice(0, 3);

          if (featured.length === 0) {
            setFeaturedMissions(operationsResponse.operations.slice(0, 3));
          } else {
            setFeaturedMissions(featured);
          }
        }
        
      } catch (error) {
        console.error('Error fetching data for home page:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchTopClans = async () => {
      try {
        setClansLoading(true);
        
        // Получаем топ клан для страйкбола
        const topAirsoftClans = await clanService.getTopClansByType('airsoft', 1);
        if (topAirsoftClans && topAirsoftClans.length > 0) {
          setTopAirsoftClan(topAirsoftClans[0]);
        }

        // Получаем топ клан для пейнтбола
        const topPaintballClans = await clanService.getTopClansByType('paintball', 1);
        if (topPaintballClans && topPaintballClans.length > 0) {
          setTopPaintballClan(topPaintballClans[0]);
        }
        
      } catch (error) {
        console.error('Error fetching top clans:', error);
      } finally {
        setClansLoading(false);
      }
    };
    
    fetchData();
    fetchTopClans();
  }, []);

  const handleAddToCart = (mission: Operation) => {
    addItem({
      id: mission.id,
      name: mission.title || mission.name || 'Mission',
      description: mission.description,
      price: mission.price,
      quantity: 1,
      image: mission.images?.[0]
    });
  };

  const formatNumber = (num: number) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  
  const handleFieldAction = (action: 'register' | 'login') => {
    console.log('Field action triggered:', { 
      action, 
      field, 
      isFieldAuthenticated, 
      initializing, 
      user,
      fieldToken: localStorage.getItem('fieldToken'),
      fieldData: localStorage.getItem('fieldData')
    });
    
    if (initializing) {
      console.log('Field context still initializing, waiting...');
      return;
    }
    
    if (isFieldAuthenticated && field) {
      // Если поле уже авторизовано, перенаправляем на страницу поля
      console.log('Redirecting to field page');
      navigate(`/fields/${field.id}`);
    } else if (user) {
      console.log('User is logged in, showing error');
      showError(
        'Access Restricted',
        `You are already logged in as a player. Please log out before accessing field ${action} functionality.`
      );
    } else {
      console.log('No auth detected, redirecting to:', action === 'register' ? '/register-field' : '/login');
      navigate(action === 'register' ? '/register-field' : '/login');
    }
  };

  return (
    <PageBackground $shadow={isShadowGrid}>
      {isNightOps && <DottedOverlay />}
      {isModernWarfare && <ModernBackground />}
      <Content>
        <NotificationContainer 
          notifications={notifications} 
          onRemove={removeNotification} 
        />
      <HeroSection>
        <HeroTitle>NovaWarfare System</HeroTitle>
        <HeroSubtitle>Next-Generation Tactical Gaming Platform</HeroSubtitle>
        <HeroButton to="/operations">Explore Operations</HeroButton>
      </HeroSection>

      <Section>
        <SectionHeader>
          <SectionTitle>Latest Intel</SectionTitle>
          <SectionSubtitle>Stay updated with the latest news and announcements</SectionSubtitle>
        </SectionHeader>
        
        {loading ? (
          <LoadingSpinner>Loading intel data...</LoadingSpinner>
        ) : latestNews.length > 0 ? (
          <>
            <IntelCard to={`/intel/${latestNews[0].id}`}>
              <IntelTitle>{latestNews[0].title}</IntelTitle>
              <IntelDate>
                {new Date(latestNews[0].createdAt).toLocaleDateString()}
              </IntelDate>
            </IntelCard>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <ViewAllButton to="/intel">View All Intel</ViewAllButton>
            </div>
          </>
        ) : (
          <EmptyState>No news available at the moment</EmptyState>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Featured Missions</SectionTitle>
          <SectionSubtitle>Top-rated tactical missions for your next game</SectionSubtitle>
        </SectionHeader>
        
        {loading ? (
          <LoadingSpinner>Loading mission data...</LoadingSpinner>
        ) : featuredMissions.length > 0 ? (
          <>
            <MissionsGrid>
              {featuredMissions.map((mission) => (
                <MissionCard key={mission.id}>
                  <MissionHeader to={`/operations/${mission.id}`}>
                    <MissionCode>Mission-{mission.id.slice(-6).toUpperCase()}</MissionCode>
                    <MissionName>{mission.title || mission.name}</MissionName>
                  </MissionHeader>
                  <MissionDescription>
                    {mission.description || 'Tactical mission awaiting deployment'}
                  </MissionDescription>
                  <MissionFooter>
                    {isFieldAuthenticated ? (
                      <>
                        <MissionPrice>${mission.price}</MissionPrice>
                        <MissionButton onClick={() => handleAddToCart(mission)}>
                          Add to Cart
                        </MissionButton>
                      </>
                    ) : (
                      <MissionButton as={Link} to={`/operations/${mission.id}`}>
                        View Details
                      </MissionButton>
                    )}
                  </MissionFooter>
                </MissionCard>
              ))}
            </MissionsGrid>
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <ViewAllButton to="/operations">View All Operations</ViewAllButton>
            </div>
          </>
        ) : (
          <EmptyState>No featured missions available</EmptyState>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Top Clans</SectionTitle>
          <SectionSubtitle>Leading squads in tactical warfare</SectionSubtitle>
        </SectionHeader>
        
        {clansLoading ? (
          <LoadingSpinner>Loading clan data...</LoadingSpinner>
        ) : (
          <ClansGrid>
            {topAirsoftClan && (
              <ClanCard>
                <ClanType>Airsoft Division</ClanType>
                <ClanName>{topAirsoftClan.name}</ClanName>
                <ClanTag>[{topAirsoftClan.tag}]</ClanTag>
                <ClanStats>
                  <ClanStat>
                    <ClanStatValue>{topAirsoftClan.memberIds?.length || 0}</ClanStatValue>
                    <ClanStatLabel>Members</ClanStatLabel>
                  </ClanStat>
                  <ClanStat>
                    <ClanStatValue>{formatNumber(topAirsoftClan.points || 0)}</ClanStatValue>
                    <ClanStatLabel>Points</ClanStatLabel>
                  </ClanStat>
                </ClanStats>
                <ClanButton to={`/clans/${topAirsoftClan.id}`}>View Clan</ClanButton>
              </ClanCard>
            )}
            
            {topPaintballClan && (
              <ClanCard>
                <ClanType>Paintball Division</ClanType>
                <ClanName>{topPaintballClan.name}</ClanName>
                <ClanTag>[{topPaintballClan.tag}]</ClanTag>
                <ClanStats>
                  <ClanStat>
                    <ClanStatValue>{topPaintballClan.memberIds?.length || 0}</ClanStatValue>
                    <ClanStatLabel>Members</ClanStatLabel>
                  </ClanStat>
                  <ClanStat>
                    <ClanStatValue>{formatNumber(topPaintballClan.points || 0)}</ClanStatValue>
                    <ClanStatLabel>Points</ClanStatLabel>
                  </ClanStat>
                </ClanStats>
                <ClanButton to={`/clans/${topPaintballClan.id}`}>View Clan</ClanButton>
              </ClanCard>
            )}
          </ClansGrid>
        )}
        
        {!clansLoading && !topAirsoftClan && !topPaintballClan && (
          <EmptyState>No clan data available</EmptyState>
        )}
        
        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <ViewAllButton to="/clans">Explore All Clans</ViewAllButton>
        </div>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>For Fields</SectionTitle>
          <SectionSubtitle>Join our network of tactical gaming venues</SectionSubtitle>
        </SectionHeader>
        
        <FieldsGrid>
          <FieldCard>
            <FieldTitle>Field Registration</FieldTitle>
            <FieldDescription>
              Register your airsoft, paintball or speedball field to access exclusive missions, 
              analytics, and management tools.
            </FieldDescription>
            <FieldButtonGroup>
              {user || isFieldAuthenticated ? (
                <>
                  <FieldButtonDisabled onClick={() => handleFieldAction('register')}>
                    Register Field
                  </FieldButtonDisabled>
                  <FieldButtonDisabled onClick={() => handleFieldAction('login')}>
                    Field Login
                  </FieldButtonDisabled>
                </>
              ) : (
                <>
                  <FieldButton to="/register-field">Register Field</FieldButton>
                  <FieldButton to="/login?role=field">Field Login</FieldButton>
                </>
              )}
            </FieldButtonGroup>
          </FieldCard>
          
          <FieldCard>
            <FieldTitle>Field Benefits</FieldTitle>
            <FieldDescription>
              Get access to premium mission packs, referee management system, 
              player analytics, and marketing tools to grow your field.
            </FieldDescription>
            <FieldButtonGroup>
              <FieldButton to="/fields">Browse Fields</FieldButton>
            </FieldButtonGroup>
          </FieldCard>
        </FieldsGrid>
      </Section>
      </Content>
    </PageBackground>
  );
};

export default HomePage;
